import { lazy, Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import AdminAdaptiveLayout from "../components/admin/AdminAdaptiveLayout";
import { PublicLayout } from "../components/layout/PublicLayout";
import ClientLayout from "../components/layout/ClientLayout";
import ProtectedAdminRoute from "../components/auth/ProtectedAdminRoute";

// Lazy imports
const Home = lazy(() => import('../pages/home/page'));
const PackagesPage = lazy(() => import('../pages/packages/page'));
const PreciosPage = lazy(() => import('../pages/precios/page'));
const ClientPortal = lazy(() => import('../pages/client/portal/page'));
const ClientJobs = lazy(() => import('../pages/client/jobs/page'));
const ClientJobDetail = lazy(() => import('../pages/client/jobs/detail/page'));
const ClientContracts = lazy(() => import('../pages/client/contracts/page'));
const ClientContractDetail = lazy(() => import('../pages/client/contracts/detail/page'));
const ClientDvdPage = lazy(() => import('../pages/client/dvd/page'));
const ClientDescargasPage = lazy(() => import('../pages/client/descargas/page'));
const ClientInvitationPage = lazy(() => import('../pages/client/invitation/page'));
// Print Approvals Client
const ClientPrintApprovalsPage = lazy(() => import('../pages/client/print-approvals/page'));
const ClientPrintApprovalDetailPage = lazy(() => import('../pages/client/print-approvals/detail/page'));
const AdminLogin = lazy(() => import('../pages/admin/login/page'));
const AdminDashboard = lazy(() => import('../pages/admin/dashboard/page'));
const AdminCalendar = lazy(() => import('../pages/admin/calendar/page'));
const AdminLeads = lazy(() => import('../pages/admin/leads/page'));
const AdminAppointments = lazy(() => import('../pages/admin/appointments/page'));
const AdminClientsPage = lazy(() => import('../pages/admin/clients/page'));
const ClientTokensPage = lazy(() => import('../pages/admin/client-tokens/page'));
const AdminContractsPage = lazy(() => import('../pages/admin/contracts/page'));
const AdminPackagesPage = lazy(() => import('../pages/admin/packages/page'));
const ContractTemplatesPage = lazy(() => import('../pages/admin/contract-templates/page'));
const AdminJobsPage = lazy(() => import('../pages/admin/jobs/page'));
const AdminJobsNewPage = lazy(() => import('../pages/admin/jobs/new/page'));
const AdminJobsUploadPage = lazy(() => import('../pages/admin/jobs/upload/page'));
const AdminJobDetailPage = lazy(() => import('../pages/admin/jobs/detail/page'));
const AdminJobSelectionsPage = lazy(() => import('../pages/admin/jobs/selections/page'));
const Smtp2goSettings = lazy(() => import('../pages/admin/settings/smtp2go/page'));
const SiteContentPage = lazy(() => import('../pages/admin/site-content/page'));
const NotFound = lazy(() => import('../pages/NotFound'));
const PrintTemplatesPage = lazy(() => import('../pages/admin/print-templates/page'));
const AdminSamplePhotosPage = lazy(() => import('../pages/admin/samples/photos/page'));
const AdminSampleVideoPage = lazy(() => import('../pages/admin/samples/video/page'));
const PublicSamplePhotosPage = lazy(() => import('../pages/public/samples/photos/page'));
const PublicSampleVideoPage = lazy(() => import('../pages/public/samples/video/page'));
const TrackingPixelsPage = lazy(() => import('../pages/admin/settings/tracking-pixels/page'));
const TaxCostsSettingsPage = lazy(() => import('../pages/admin/settings/tax-costs/page'));
const AdminIndividualProductsPage = lazy(() => import('../pages/admin/individual-products/page'));
const PublicProductosPage = lazy(() => import('../pages/productos/page'));
const EmailCampaignsPage = lazy(() => import('../pages/admin/email-campaigns/page'));
const AdminHelpVideosPage = lazy(() => import('../pages/admin/help-videos/page'));
const HelpPage = lazy(() => import('../pages/ayuda/page'));
const TermsPage = lazy(() => import('../pages/terms/page'));
const DvdBuilderPage = lazy(() => import('../pages/admin/dvd-builder/page'));
const AdminDvdsPage = lazy(() => import('../pages/admin/dvds/page'));
const DVDPreviewPage = lazy(() => import('../pages/admin/dvds/preview/page'));
const DVDRedirectPage = lazy(() => import('../pages/dvd-redirect/page'));
const TVCodeEntryPage = lazy(() => import('../pages/tv/page'));
const TVPlayerPage = lazy(() => import('../pages/tv/player/page'));
const TVConnectPage = lazy(() => import('../pages/tv/connect/page'));
const InvitationPublic = lazy(() => import('../pages/public/InvitationPublic'));
const PremiumInvitationPage = lazy(() => import('../pages/public/PremiumInvitationPage'));
const PoseGuidesAdminPage = lazy(() => import('../pages/admin/pose-guides/page'));
const InvitationsAdminPage = lazy(() => import('../pages/admin/invitations/page'));
const PoseGuidesListPage = lazy(() => import('../pages/client/pose-guides/page'));
const PoseGuideViewerPage = lazy(() => import('../pages/client/pose-guides/viewer'));
const AdminEntregasPage = lazy(() => import('../pages/admin/entregas/page'));
const AdminStorageManagerPage = lazy(() => import('../pages/admin/storage-manager/page'));
const SystemAuditorPage = lazy(() => import('../pages/admin/system-auditor/page'));
const AdminPhotobooksPage = lazy(() => import('../pages/admin/photobooks/page'));
const AdminPhotobookFramesPage = lazy(() => import('../pages/admin/photobook-frames/page'));
const ClientPhotobooksPage = lazy(() => import('../pages/client/photobooks/page'));
const PhotobookViewer = lazy(() => import('../pages/client/photobooks/viewer'));
const ClientHomePage = lazy(() => import('../pages/client/home/ClientHomePage'));
const ClientSessionDetail = lazy(() => import('../pages/client/sessions/detail/page'));
const ClientSessionsPage = lazy(() => import('../pages/client/sessions/page'));
const ClientProduccionPage = lazy(() => import('../pages/client/produccion/page'));
const AdminEventAttentionPage = lazy(() => import('../pages/admin/event-attention/page'));
const AdminProduccionPage = lazy(() => import('../pages/admin/produccion/page'));
const AdminPreciosPage = lazy(() => import('../pages/admin/precios/page'));
const PhotobookPublicBookViewer = lazy(() => import('../pages/public/PhotobookPublicBookViewer'));
const PhotobookGuestPage = lazy(() => import('../pages/public/PhotobookGuestPage'));
const AdminGuestbookPage = lazy(() => import('../pages/admin/photobooks/guestbook/page'));
const AddonsConfigPage = lazy(() => import('../pages/admin/service-config/page'));
const AdminPaymentsPage = lazy(() => import('../pages/admin/payments/AdminPaymentsPage'));
const AdminSettingsPage = lazy(() => import('../pages/admin/settings/AdminSettingsPage'));
const AdminTvAppsPage = lazy(() => import('../pages/admin/tv-apps/page'));
const TvAppsPublicPage = lazy(() => import('../pages/tv-apps/page'));
const AdminSessions = lazy(() => import('../pages/admin/sessions/page'));

const RuletaPremiosPage = lazy(() => import('../pages/admin/ruleta-premios/page'));

// StudioFlow AI
const StudioFlowDashboard = lazy(() => import('../pages/admin/studioflow/dashboard/page'));
const StudioFlowWorkflows = lazy(() => import('../pages/admin/studioflow/workflows/page'));
const StudioFlowPhotoshop = lazy(() => import('../pages/admin/studioflow/photoshop/page'));
const StudioFlowLightroom = lazy(() => import('../pages/admin/studioflow/lightroom/page'));
const StudioFlowResolve = lazy(() => import('../pages/admin/studioflow/resolve/page'));
const StudioFlowFiles = lazy(() => import('../pages/admin/studioflow/files/page'));
const StudioFlowLogs = lazy(() => import('../pages/admin/studioflow/logs/page'));
const StudioFlowConnections = lazy(() => import('../pages/admin/studioflow/connections/page'));
const StudioFlowSettings = lazy(() => import('../pages/admin/studioflow/settings/page'));
const StudioFlowWorkflowEditor = lazy(() => import('../pages/admin/studioflow/workflows/editor/page'));
const StudioFlowAIEngine = lazy(() => import('../pages/admin/studioflow/ai-engine/page'));
const StudioFlowProcessPhotos = lazy(() => import('../pages/admin/studioflow/process-photos/page'));
const StudioFlowProcessVideo = lazy(() => import('../pages/admin/studioflow/process-video/page'));
const StudioFlowReviewResults = lazy(() => import('../pages/admin/studioflow/review-results/page'));
const StudioFlowProductionHistory = lazy(() => import('../pages/admin/studioflow/production-history/page'));

// Print Approvals
const PrintApprovalsPage = lazy(() => import('../pages/admin/print-approvals/page'));
const PrintApprovalDetailPage = lazy(() => import('../pages/admin/print-approvals/detail/page'));
const PrintApprovalsNewPage = lazy(() => import('../pages/admin/print-approvals/new/page'));

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      <p className="mt-4 text-gray-600">Cargando...</p>
    </div>
  </div>
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      {
        index: true,
        element: <Suspense fallback={<LoadingFallback />}><Home /></Suspense>
      },
      {
        path: 'paquetes',
        element: <Suspense fallback={<LoadingFallback />}><PackagesPage /></Suspense>
      },
      {
        path: 'productos',
        element: <Suspense fallback={<LoadingFallback />}><PublicProductosPage /></Suspense>
      },
      {
        path: 'muestras/fotos',
        element: <Suspense fallback={<LoadingFallback />}><PublicSamplePhotosPage /></Suspense>
      },
      {
        path: 'muestras/video',
        element: <Suspense fallback={<LoadingFallback />}><PublicSampleVideoPage /></Suspense>
      },
      {
        path: 'ayuda',
        element: <Suspense fallback={<LoadingFallback />}><HelpPage /></Suspense>
      },
      {
        path: 'terms.html',
        element: <Suspense fallback={<LoadingFallback />}><TermsPage /></Suspense>
      },
      {
        path: 'tv-apps',
        element: <Suspense fallback={<LoadingFallback />}><TvAppsPublicPage /></Suspense>
      }
    ]
  },
  {
    path: '/precios',
    element: <Suspense fallback={<LoadingFallback />}><PreciosPage /></Suspense>
  },
  {
    path: '/portal',
    element: <Suspense fallback={<LoadingFallback />}><ClientPortal /></Suspense>
  },
  {
    path: '/dvd/:shortCode',
    element: <Suspense fallback={<LoadingFallback />}><DVDRedirectPage /></Suspense>
  },
  {
    path: '/client/:token',
    element: <ClientLayout />,
    children: [
      {
        index: true,
        element: <Suspense fallback={<LoadingFallback />}><ClientHomePage /></Suspense>
      },
      {
        path: 'jobs',
        element: <Suspense fallback={<LoadingFallback />}><ClientJobs /></Suspense>
      },
      {
        path: 'jobs/:jobId',
        element: <Suspense fallback={<LoadingFallback />}><ClientJobDetail /></Suspense>
      },
      {
        path: 'contracts',
        element: <Suspense fallback={<LoadingFallback />}><ClientContracts /></Suspense>
      },
      {
        path: 'contracts/:contractId',
        element: <Suspense fallback={<LoadingFallback />}><ClientContractDetail /></Suspense>
      },
      {
        path: 'dvd',
        element: <Suspense fallback={<LoadingFallback />}><ClientDvdPage /></Suspense>
      },
      {
        path: 'descargas',
        element: <Suspense fallback={<LoadingFallback />}><ClientDescargasPage /></Suspense>
      },
      {
        path: 'invitation',
        element: <Suspense fallback={<LoadingFallback />}><ClientInvitationPage /></Suspense>
      },
      {
        path: 'pose-guides',
        element: <Suspense fallback={<LoadingFallback />}><PoseGuidesListPage /></Suspense>
      },
      {
        path: 'pose-guide/:guideId',
        element: <Suspense fallback={<LoadingFallback />}><PoseGuideViewerPage /></Suspense>
      },
      {
        path: 'photobooks',
        element: <Suspense fallback={<LoadingFallback />}><ClientPhotobooksPage /></Suspense>
      },
      {
        path: 'photobooks/:id',
        element: <Suspense fallback={<LoadingFallback />}><PhotobookViewer /></Suspense>
      },
      {
        path: 'print-approvals',
        element: <Suspense fallback={<LoadingFallback />}><ClientPrintApprovalsPage /></Suspense>
      },
      {
        path: 'print-approvals/:id',
        element: <Suspense fallback={<LoadingFallback />}><ClientPrintApprovalDetailPage /></Suspense>
      },
      {
        path: 'sessions',
        element: <Suspense fallback={<LoadingFallback />}><ClientSessionsPage /></Suspense>
      },
      {
        path: 'sessions/:sessionId',
        element: <Suspense fallback={<LoadingFallback />}><ClientSessionDetail /></Suspense>
      },
      {
        path: 'produccion',
        element: <Suspense fallback={<LoadingFallback />}><ClientProduccionPage /></Suspense>
      }
    ]
  },
  {
    path: '/admin',
    element: <ProtectedAdminRoute><AdminAdaptiveLayout /></ProtectedAdminRoute>,
    children: [
      {
        index: true,
        element: <Navigate to="dashboard" replace />
      },
      {
        path: 'dashboard',
        element: <Suspense fallback={<LoadingFallback />}><AdminDashboard /></Suspense>
      },
      {
        path: 'appointments',
        element: <Suspense fallback={<LoadingFallback />}><AdminAppointments /></Suspense>
      },
      {
        path: 'calendar',
        element: <Suspense fallback={<LoadingFallback />}><AdminCalendar /></Suspense>
      },
      {
        path: 'sessions',
        element: <Suspense fallback={<LoadingFallback />}><AdminSessions /></Suspense>
      },
      {
        path: 'leads',
        element: <Suspense fallback={<LoadingFallback />}><AdminLeads /></Suspense>
      },
      {
        path: 'clients',
        element: <Suspense fallback={<LoadingFallback />}><AdminClientsPage /></Suspense>
      },
      {
        path: 'client-tokens',
        element: <Suspense fallback={<LoadingFallback />}><ClientTokensPage /></Suspense>
      },
      {
        path: 'contracts',
        element: <Suspense fallback={<LoadingFallback />}><AdminContractsPage /></Suspense>
      },
      {
        path: 'contract-templates',
        element: <Suspense fallback={<LoadingFallback />}><ContractTemplatesPage /></Suspense>
      },
      {
        path: 'packages',
        element: <Suspense fallback={<LoadingFallback />}><AdminPackagesPage /></Suspense>
      },
      {
        path: 'individual-products',
        element: <Suspense fallback={<LoadingFallback />}><AdminIndividualProductsPage /></Suspense>
      },
      {
        path: 'email-campaigns',
        element: <Suspense fallback={<LoadingFallback />}><EmailCampaignsPage /></Suspense>
      },
      {
        path: 'site-content',
        element: <Suspense fallback={<LoadingFallback />}><SiteContentPage /></Suspense>
      },
      {
        path: 'print-templates',
        element: <Suspense fallback={<LoadingFallback />}><PrintTemplatesPage /></Suspense>
      },
      {
        path: 'samples/photos',
        element: <Suspense fallback={<LoadingFallback />}><AdminSamplePhotosPage /></Suspense>
      },
      {
        path: 'samples/video',
        element: <Suspense fallback={<LoadingFallback />}><AdminSampleVideoPage /></Suspense>
      },
      {
        path: 'jobs',
        element: <Suspense fallback={<LoadingFallback />}><AdminJobsPage /></Suspense>
      },
      {
        path: 'jobs/new',
        element: <Suspense fallback={<LoadingFallback />}><AdminJobsNewPage /></Suspense>
      },
      {
        path: 'jobs/:id',
        element: <Suspense fallback={<LoadingFallback />}><AdminJobDetailPage /></Suspense>
      },
      {
        path: 'jobs/:id/upload',
        element: <Suspense fallback={<LoadingFallback />}><AdminJobsUploadPage /></Suspense>
      },
      {
        path: 'jobs/:id/selections',
        element: <Suspense fallback={<LoadingFallback />}><AdminJobSelectionsPage /></Suspense>
      },
      {
        path: 'entregas',
        element: <Suspense fallback={<LoadingFallback />}><AdminEntregasPage /></Suspense>
      },
      {
        path: 'storage-manager',
        element: <Suspense fallback={<LoadingFallback />}><AdminStorageManagerPage /></Suspense>
      },
      {
        path: 'settings/smtp2go',
        element: <Suspense fallback={<LoadingFallback />}><Smtp2goSettings /></Suspense>
      },
      {
        path: 'settings/tracking-pixels',
        element: <Suspense fallback={<LoadingFallback />}><TrackingPixelsPage /></Suspense>
      },
      {
        path: 'settings/tax-costs',
        element: <Suspense fallback={<LoadingFallback />}><TaxCostsSettingsPage /></Suspense>
      },
      {
        path: 'system-auditor',
        element: <Suspense fallback={<LoadingFallback />}><SystemAuditorPage /></Suspense>
      },
      {
        path: 'help-videos',
        element: <Suspense fallback={<LoadingFallback />}><AdminHelpVideosPage /></Suspense>
      },
      {
        path: 'ruleta-premios',
        element: <Suspense fallback={<LoadingFallback />}><RuletaPremiosPage /></Suspense>
      },
      {
        path: 'dvd-builder',
        element: <Suspense fallback={<LoadingFallback />}><DvdBuilderPage /></Suspense>
      },
      {
        path: 'dvds',
        element: <Suspense fallback={<LoadingFallback />}><AdminDvdsPage /></Suspense>
      },
      {
        path: 'dvds/preview',
        element: <Suspense fallback={<LoadingFallback />}><DVDPreviewPage /></Suspense>
      },
      {
        path: 'pose-guides',
        element: <Suspense fallback={<LoadingFallback />}><PoseGuidesAdminPage /></Suspense>
      },
      {
        path: 'invitations',
        element: <Suspense fallback={<LoadingFallback />}><InvitationsAdminPage /></Suspense>
      },
      {
        path: 'photobooks',
        element: <Suspense fallback={<LoadingFallback />}><AdminPhotobooksPage /></Suspense>
      },
      {
        path: 'photobooks/guestbook',
        element: <Suspense fallback={<LoadingFallback />}><AdminGuestbookPage /></Suspense>
      },
      {
        path: 'photobook-frames',
        element: <Suspense fallback={<LoadingFallback />}><AdminPhotobookFramesPage /></Suspense>
      },
      {
        path: 'event-attention',
        element: <Suspense fallback={<LoadingFallback />}><AdminEventAttentionPage /></Suspense>
      },
      {
        path: 'produccion',
        element: <Suspense fallback={<LoadingFallback />}><AdminProduccionPage /></Suspense>
      },
      {
        path: 'precios',
        element: <Suspense fallback={<LoadingFallback />}><AdminPreciosPage /></Suspense>
      },
      {
        path: 'service-config',
        element: <Suspense fallback={<LoadingFallback />}><AddonsConfigPage /></Suspense>
      },
      {
        path: 'payments',
        element: <Suspense fallback={<LoadingFallback />}><AdminPaymentsPage /></Suspense>
      },
      {
        path: 'settings',
        element: <Suspense fallback={<LoadingFallback />}><AdminSettingsPage /></Suspense>
      },
      {
        path: 'tv-apps',
        element: <Suspense fallback={<LoadingFallback />}><AdminTvAppsPage /></Suspense>
      },
      {
        path: 'print-approvals',
        element: <Suspense fallback={<LoadingFallback />}><PrintApprovalsPage /></Suspense>
      },
      {
        path: 'print-approvals/new',
        element: <Suspense fallback={<LoadingFallback />}><PrintApprovalsNewPage /></Suspense>
      },
      {
        path: 'print-approvals/:id',
        element: <Suspense fallback={<LoadingFallback />}><PrintApprovalDetailPage /></Suspense>
      },
      {
        path: 'studioflow',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowDashboard /></Suspense>
      },
      {
        path: 'studioflow/workflows',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowWorkflows /></Suspense>
      },
      {
        path: 'studioflow/photoshop',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowPhotoshop /></Suspense>
      },
      {
        path: 'studioflow/lightroom',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowLightroom /></Suspense>
      },
      {
        path: 'studioflow/resolve',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowResolve /></Suspense>
      },
      {
        path: 'studioflow/files',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowFiles /></Suspense>
      },
      {
        path: 'studioflow/logs',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowLogs /></Suspense>
      },
      {
        path: 'studioflow/connections',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowConnections /></Suspense>
      },
      {
        path: 'studioflow/settings',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowSettings /></Suspense>
      },
      {
        path: 'studioflow/workflows/create',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowWorkflowEditor /></Suspense>
      },
      {
        path: 'studioflow/workflows/edit/:id',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowWorkflowEditor /></Suspense>
      },
      {
        path: 'studioflow/ai-engine',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowAIEngine /></Suspense>
      },
      {
        path: 'studioflow/process-photos',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowProcessPhotos /></Suspense>
      },
      {
        path: 'studioflow/process-video',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowProcessVideo /></Suspense>
      },
      {
        path: 'studioflow/review-results',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowReviewResults /></Suspense>
      },
      {
        path: 'studioflow/production-history',
        element: <Suspense fallback={<LoadingFallback />}><StudioFlowProductionHistory /></Suspense>
      }
    ]
  },
  {
    path: '/admin/login',
    element: <Suspense fallback={<LoadingFallback />}><AdminLogin /></Suspense>
  },
  {
    /* Primary public album viewer — same real book, no login required */
    path: '/album/:photobookId',
    element: <Suspense fallback={<LoadingFallback />}><PhotobookPublicBookViewer /></Suspense>
  },
  {
    /* Guestbook page — standalone elegant page for leaving dedications */
    path: '/guestbook/:publicToken',
    element: <Suspense fallback={<LoadingFallback />}><PhotobookGuestPage /></Suspense>
  },
  {
    /* Legacy firmas route — shows guestbook page */
    path: '/firmas/:photobookId',
    element: <Suspense fallback={<LoadingFallback />}><PhotobookGuestPage /></Suspense>
  },
  {
    /* Legacy messages route — shows guestbook page */
    path: '/photobook/:photobookId/messages',
    element: <Suspense fallback={<LoadingFallback />}><PhotobookGuestPage /></Suspense>
  },
  {
    path: '/invite/:id',
    element: <Suspense fallback={<LoadingFallback />}><InvitationPublic /></Suspense>
  },
  {
    path: '/preview-invite/:id',
    element: <Suspense fallback={<LoadingFallback />}><InvitationPublic /></Suspense>
  },
  {
    path: '/tv',
    element: <Suspense fallback={<LoadingFallback />}><TVCodeEntryPage /></Suspense>
  },
  {
    path: '/tv/connect/:pairingToken',
    element: <Suspense fallback={<LoadingFallback />}><TVConnectPage /></Suspense>
  },
  {
    path: '/tv/:code',
    element: <Suspense fallback={<LoadingFallback />}><TVPlayerPage /></Suspense>
  },
  {
    path: '*',
    element: <Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>
  }
];

export default routes;
